import React, { useEffect } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { injectConversionTracking, injectSocialTags, removeConversionTracking } from '../utils/socialTags';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const GlobalStyle = createGlobalStyle`
  body {
    background: linear-gradient(180deg, #f7f8fc 0%, #f1f2f8 100%) fixed;
    min-height: 100vh;
    margin: 0;
  }
`;

const Container = styled.div`
  max-width: 540px;
  margin: 0 auto;
  padding: 44px 20px 72px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  color: #0e1726;
  animation: ${fadeIn} 0.4s ease-out;
`;

const Brand = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const BrandName = styled.div`
  font-family: "Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  font-weight: 700;
  font-size: 21px;
  letter-spacing: -0.5px;
  color: #0e1726;
`;

const BrandTag = styled.div`
  font-size: 8.5px;
  font-weight: 600;
  letter-spacing: 2.5px;
  color: #98a1b2;
  margin-top: -3px;
  text-transform: uppercase;
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid #e9ebf1;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(14, 23, 38, 0.04), 0 18px 40px -24px rgba(14, 23, 38, 0.18);
  overflow: hidden;
`;

const Pad = styled.div`
  padding: 34px 30px;
  @media (max-width: 520px) {
    padding: 28px 20px;
  }
`;

const TopSection = styled.div`
  text-align: center;
  padding-bottom: 6px;
`;

const CheckCircle = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: #e7f7ee;
  display: grid;
  place-items: center;
  margin: 0 auto 20px;
`;

const Title = styled.h1`
  font-family: "Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.035em;
  margin: 0 0 10px;
  color: #0e1726;
  @media (max-width: 520px) {
    font-size: 26px;
  }
`;

const Lede = styled.p`
  color: #5a6478;
  font-size: 14.5px;
  line-height: 1.6;
  margin: 0 auto;
  max-width: 400px;
  & b {
    color: #0e1726;
    font-weight: 600;
  }
`;

const Ref = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
  font-size: 12.5px;
  color: #5a6478;
`;

const RefId = styled.span`
  font-weight: 700;
  color: #0e1726;
  font-variant-numeric: tabular-nums;
`;

const HoldNote = styled.div`
  display: flex;
  gap: 10px;
  background: #f4f4f5;
  border: 1px solid #e9ebf1;
  border-left: 3px solid #0b0b0c;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 16px;
  align-items: flex-start;
`;

const HoldNoteIcon = styled.svg`
  flex: 0 0 16px;
  color: #0b0b0c;
  margin-top: 1px;
`;

const HoldNoteText = styled.div`
  font-size: 12.5px;
  color: #5a6478;
  line-height: 1.55;
  & b {
    color: #0e1726;
    font-weight: 600;
  }
`;

const MiniSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 18px 0 4px;
`;

const Thumb = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #f4f4f5;
  border: 1px solid #e9ebf1;
  flex: 0 0 42px;
  display: grid;
  place-items: center;
  color: #0e1726;
`;

const MiniName = styled.div`
  font-weight: 600;
  flex: 1;
  font-size: 14px;
  color: #0e1726;
`;

const MiniAmt = styled.div`
  text-align: right;
  white-space: nowrap;
`;

const MiniAmtValue = styled.div`
  font-family: "Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  font-weight: 700;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
`;

const MiniAmtLabel = styled.div`
  font-size: 11px;
  color: #98a1b2;
  font-weight: 500;
`;

const Divider = styled.div`
  height: 1px;
  background: #e9ebf1;
  margin: 0;
`;

const Eyebrow = styled.div`
  font-family: "Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #98a1b2;
  margin-bottom: 16px;
`;

const StepsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
`;

const Step = styled.li`
  position: relative;
  padding: 0 0 20px 40px;
  &:last-child {
    padding-bottom: 0;
  }
`;

const StepCircle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0e1726;
  color: #fff;
  font-family: "Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  font-weight: 700;
  font-size: 12px;
  display: grid;
  place-items: center;
`;

const StepLine = styled.div`
  position: absolute;
  left: 13px;
  top: 30px;
  bottom: 6px;
  width: 2px;
  background: #e9ebf1;
  ${Step}:last-child & {
    display: none;
  }
`;

const StepTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
  color: #0e1726;
`;

const StepDesc = styled.div`
  font-size: 13px;
  color: #5a6478;
  line-height: 1.5;
`;

const Actions = styled.div`
  padding: 24px 30px 30px;
  @media (max-width: 520px) {
    padding: 20px 20px 26px;
  }
`;

const PrimaryButton = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  text-decoration: none;
  background: #0b0b0c;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 15px;
  font-family: "Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 20px -10px rgba(11, 11, 12, 0.35);
  box-sizing: border-box;
  &:hover {
    background: #26262a;
  }
`;

const SecondaryButton = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  text-decoration: none;
  background: none;
  color: #0e1726;
  border: none;
  padding: 13px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  margin-top: 4px;
  box-sizing: border-box;
  &:hover {
    color: #5a6478;
  }
`;

const Foot = styled.div`
  text-align: center;
  font-size: 12.5px;
  color: #98a1b2;
  margin-top: 22px;
  line-height: 1.6;
  & b {
    color: #5a6478;
    font-weight: 600;
  }
`;

const EmailLink = styled.a`
  color: inherit;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ThankYouPage = ({ clientConfig }) => {
  useEffect(() => {
    const id = 'tky-google-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.href = 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600;700&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let retryInterval = null;
    const cachedConversionTag = sessionStorage.getItem('wellie_conv_tag') || '';
    const cachedGlobalTag = sessionStorage.getItem('wellie_global_tag') || '';
    const effectiveConfig = {
      ...(clientConfig || {}),
      custom_global_js:
        (typeof clientConfig?.custom_global_js === 'string' && clientConfig.custom_global_js.trim())
          ? clientConfig.custom_global_js
          : cachedGlobalTag,
      conversion_tracking_js:
        (typeof clientConfig?.conversion_tracking_js === 'string' && clientConfig.conversion_tracking_js.trim())
          ? clientConfig.conversion_tracking_js
          : cachedConversionTag,
    };

    const extractPixelId = () => {
      try {
        const sources = [
          effectiveConfig?.custom_global_js || '',
          effectiveConfig?.conversion_tracking_js || '',
          sessionStorage.getItem('wellie_global_tag') || '',
          sessionStorage.getItem('wellie_conv_tag') || '',
        ];
        for (const source of sources) {
          if (!source) continue;
          const byFbqInit = source.match(/fbq\(\s*['"]init['"]\s*,\s*['"](\d+)['"]\s*\)/i);
          if (byFbqInit?.[1]) return byFbqInit[1];
          const byTrId = source.match(/[?&]id=(\d{8,})/i);
          if (byTrId?.[1]) return byTrId[1];
        }
      } catch (e) {
        console.warn('⚠️ Pixel ID extraction failed:', e);
      }
      return '';
    };

    const installFbqPurchaseObserver = () => {
      if (typeof window.fbq !== 'function' || window.fbq.__welliePurchaseObserver) {
        return;
      }

      const originalFbq = window.fbq;
      const observedFbq = function observedFbq(...args) {
        try {
          if (
            String(args[0] || '').toLowerCase() === 'track'
            && String(args[1] || '').toLowerCase() === 'purchase'
          ) {
            window.__welliePurchaseTracked = true;
          }
        } catch (_) {
          // Observation is best-effort only.
        }
        return originalFbq.apply(this, args);
      };

      Object.assign(observedFbq, originalFbq);
      observedFbq.__welliePurchaseObserver = true;
      window.fbq = observedFbq;
    };

    const emitGuaranteedPurchase = () => {
      try {
        const conversion = window.welliemdConversion || {};
        const orderId = conversion.order_id || '';
        const amount = Number(conversion.amount || 0);
        const buyerName = typeof conversion.buyer_name === 'string' ? conversion.buyer_name : '';
        const buyerEmail = typeof conversion.buyer_email === 'string' ? conversion.buyer_email : '';
        const product = typeof conversion.product === 'string' ? conversion.product : '';
        const couponCode = typeof conversion.coupon_code === 'string' ? conversion.coupon_code : '';
        const dedupeKey = orderId
          ? `wellie_purchase_sent_${orderId}`
          : `wellie_purchase_sent_fallback_${amount}`;

        if (sessionStorage.getItem(dedupeKey) === '1' || window.__welliePurchaseTracked) {
          return true;
        }

        const normalizedAmount = Number.isFinite(amount) ? amount : 0;
        let dispatched = false;

        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            value: normalizedAmount,
            currency: 'USD',
            buyer_name: buyerName,
            buyer_email: buyerEmail,
            order_id: orderId,
            amount: normalizedAmount,
            product,
            coupon_code: couponCode,
          });
          window.__welliePurchaseTracked = true;
          dispatched = true;
        }

        const pixelId = extractPixelId();
        if (!dispatched && pixelId) {
          const params = new URLSearchParams({
            id: pixelId,
            ev: 'Purchase',
            noscript: '1',
          });
          params.set('cd[value]', String(normalizedAmount));
          params.set('cd[currency]', 'USD');
          if (buyerName) params.set('cd[buyer_name]', buyerName);
          if (buyerEmail) params.set('cd[buyer_email]', buyerEmail);
          if (orderId) params.set('cd[order_id]', orderId);
          if (product) params.set('cd[product]', product);
          if (couponCode) params.set('cd[coupon_code]', couponCode);

          const beacon = new Image();
          beacon.src = `https://www.facebook.com/tr?${params.toString()}`;
          dispatched = true;
        } else if (!dispatched) {
          console.warn('⚠️ Guaranteed Purchase fallback skipped: pixel id not found');
        }

        if (!dispatched) {
          return false;
        }

        sessionStorage.setItem(dedupeKey, '1');
        console.log('✅ Guaranteed Purchase event dispatched');
        return true;
      } catch (e) {
        console.warn('⚠️ Guaranteed Purchase dispatch failed:', e);
        return false;
      }
    };

    const startGuaranteedPurchaseRetry = () => {
      if (window.__welliePurchaseTracked) {
        return;
      }

      if (emitGuaranteedPurchase()) {
        return;
      }

      let attempts = 0;
      const maxAttempts = 10;
      retryInterval = setInterval(() => {
        attempts += 1;
        const done = emitGuaranteedPurchase();
        if (done || attempts >= maxAttempts) {
          clearInterval(retryInterval);
          retryInterval = null;
        }
      }, 300);
    };

    console.log(' Thank You page mounted, injecting conversion tracking if available');
    window.__welliePurchaseTracked = false;
    injectSocialTags(effectiveConfig)
      .then(() => {
        installFbqPurchaseObserver();
        return injectConversionTracking(effectiveConfig);
      })
      .then(() => {
        if (isMounted) {
          installFbqPurchaseObserver();
          setTimeout(startGuaranteedPurchaseRetry, 250);
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.error('❌ Conversion tracking injection failed:', error);
          startGuaranteedPurchaseRetry();
        }
      });

    return () => {
      isMounted = false;
      if (retryInterval) {
        clearInterval(retryInterval);
      }
      console.log('Thank You page unmounted, removing conversion tracking');
      removeConversionTracking();
    };
  }, [clientConfig]);

  let orderId = '';
  let orderUuid = '';
  let productName = '';
  let orderAmount = '';
  try {
    const rawData = sessionStorage.getItem('welliemd_conversion_data');
    if (rawData) {
      const data = JSON.parse(rawData);
      orderId = data.order_id || '';
      orderUuid = data.order_uuid || '';
      productName = data.product || '';
      orderAmount = data.amount || '';
    }
  } catch (e) {
    // sessionStorage not available or corrupted data
  }

  const companyName =
    clientConfig?.questionnaire_config?.company_name ||
    clientConfig?.name ||
    'Kin Meds';

  const nameParts = companyName.split(' ');
  const brandPrimary = nameParts[0];
  const brandSecondary = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

  const supportEmail =
    clientConfig?.support_email ||
    clientConfig?.email ||
    clientConfig?.questionnaire_config?.support_email ||
    clientConfig?.questionnaire_config?.email;

  const patientPortalDomain = clientConfig?.patient_portal_domain || '';
  const orderStatusUrl = patientPortalDomain
    ? `${patientPortalDomain}/dashboard/orders${orderUuid ? `/${encodeURIComponent(orderUuid)}` : ''}`
    : '#';
  const accountUrl = patientPortalDomain
    ? patientPortalDomain
    : '#';

  const parsedAmount = parseFloat(orderAmount);
  const formattedAmount = orderAmount && !isNaN(parsedAmount)
    ? `$${parsedAmount.toFixed(2)}`
    : '';

  return (
    <>
      <GlobalStyle />
      <Container>
      <Brand>
        <BrandName>{brandPrimary}</BrandName>
        {brandSecondary && <BrandTag>{brandSecondary}</BrandTag>}
      </Brand>

      <Panel>
        <Pad>
          <TopSection>
            <CheckCircle>
              <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.6" width="30" height="30">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </CheckCircle>
            <Title>Order received</Title>
            <Lede>
              Thanks — we've got your request and sent it to a licensed provider for review.{' '}
              <b>You're only charged for what your provider prescribes.</b>
            </Lede>
            {orderId && (
              <Ref>
                Reference <RefId>#{orderId}</RefId>
              </Ref>
            )}
          </TopSection>

          <HoldNote>
            <HoldNoteIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M12 3l7 3v6c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" />
            </HoldNoteIcon>
            <HoldNoteText>
              <b>A temporary hold was placed — not a charge.</b> Your card is only charged once a provider approves your order.
            </HoldNoteText>
          </HoldNote>

          {productName && (
            <MiniSummary>
              <Thumb>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                  <rect x="4" y="4" width="16" height="16" rx="4" />
                  <path d="M9 9h6M9 13h6" />
                </svg>
              </Thumb>
              <MiniName>
                {productName}
              </MiniName>
              {formattedAmount && (
                <MiniAmt>
                  <MiniAmtValue>{formattedAmount}</MiniAmtValue>
                  <MiniAmtLabel>on hold today</MiniAmtLabel>
                </MiniAmt>
              )}
            </MiniSummary>
          )}
        </Pad>

        <Divider />

        <Pad>
          <Eyebrow>What happens next</Eyebrow>
          <StepsList>
            <Step>
              <StepCircle>1</StepCircle>
              <StepLine />
              <StepTitle>A provider reviews your request</StepTitle>
              <StepDesc>A licensed provider checks your intake — usually within 24 hours.</StepDesc>
            </Step>
            <Step>
              <StepCircle>2</StepCircle>
              <StepLine />
              <StepTitle>Your prescription is finalized</StepTitle>
              <StepDesc>If approved, your card is charged for the prescribed treatment. We'll email you the details.</StepDesc>
            </Step>
            <Step>
              <StepCircle>3</StepCircle>
              <StepTitle>Your order ships</StepTitle>
              <StepDesc>Your medication is sent from the pharmacy and you'll get tracking by email and text.</StepDesc>
            </Step>
          </StepsList>
        </Pad>

        <Actions>
          <PrimaryButton href={orderStatusUrl} target={patientPortalDomain ? '_blank' : undefined} rel="noopener noreferrer">
            View order status
          </PrimaryButton>
          <SecondaryButton href={accountUrl} target={patientPortalDomain ? '_blank' : undefined} rel="noopener noreferrer">
            Return to my account
          </SecondaryButton>
        </Actions>
      </Panel>

      <Foot>
        Questions about your order?{' '}
        {supportEmail ? (
          <b><EmailLink href={`mailto:${supportEmail}`}>{supportEmail}</EmailLink></b>
        ) : (
          <b>Contact our support team.</b>
        )}
      </Foot>
    </Container>
    </>
  );
};

export default ThankYouPage;
